import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock, FileText, Loader, ChevronDown, X, Flame, Upload } from 'lucide-react';
import { burnCashTokenRequestsAPI } from '../../services/burnCashTokenRequestsAPI';

// Modal Component
const Modal = ({ isOpen, onClose, title, message, type = 'info', onConfirm, confirmText, cancelText, showInput, inputPlaceholder }) => {
    const [inputValue, setInputValue] = useState('');

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm(showInput ? inputValue : true);
        }
        setInputValue('');
    };

    const handleClose = () => {
        setInputValue('');
        onClose();
    };

    const typeStyles = {
        success: {
            icon: CheckCircle,
            iconColor: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            buttonColor: 'bg-green-600 hover:bg-green-700'
        },
        error: {
            icon: XCircle,
            iconColor: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            buttonColor: 'bg-red-600 hover:bg-red-700'
        },
        warning: {
            icon: AlertTriangle,
            iconColor: 'text-yellow-600',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        },
        info: {
            icon: FileText,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            buttonColor: 'bg-blue-600 hover:bg-blue-700'
        }
    };

    const style = typeStyles[type] || typeStyles.info;
    const Icon = style.icon;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <X className="h-5 w-5" />
                </button>

                <div className="p-6">
                    <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${style.bgColor} ${style.borderColor} border-2 mb-4`}>
                        <Icon className={`h-6 w-6 ${style.iconColor}`} />
                    </div>

                    {title && (
                        <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                            {title}
                        </h3>
                    )}

                    <p className="text-sm text-gray-600 text-center mb-6">
                        {message}
                    </p>

                    {showInput && (
                        <div className="mb-6">
                            <textarea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={inputPlaceholder || 'Enter notes...'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                autoFocus
                            />
                        </div>
                    )}

                    <div className="flex space-x-3">
                        {onConfirm && (
                            <button
                                onClick={handleConfirm}
                                className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-md ${style.buttonColor} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                            >
                                {confirmText || 'Confirm'}
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                        >
                            {cancelText || 'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const IssuancePaymentManagement = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: '',
        onConfirm: null,
        confirmText: '',
        showInput: false,
        inputPlaceholder: ''
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
        }, 400);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        fetchRequests({
            page: currentPage,
            limit: pageSize,
            status: statusFilter,
            search: debouncedSearch
        });
    }, [currentPage, pageSize, statusFilter, debouncedSearch]);

    const fetchRequests = async ({ page = 1, limit = 10, status = 'all', search = '' } = {}) => {
        try {
            setLoading(true);
            setError(null);
            const response = await burnCashTokenRequestsAPI.getAllRequests({
                page,
                limit,
                status: status !== 'all' ? status : undefined,
                search: search || undefined
            });

            const requestData = response.data || [];
            setRequests(requestData);
            setTotalItems(response.meta?.total ?? requestData.length);
            setTotalPages(response.meta?.totalPages ?? 0);

            if (response.meta?.page && response.meta.page !== currentPage) {
                setCurrentPage(response.meta.page);
            }
        } catch (err) {
            console.error('Error fetching issuance payment management requests:', err);
            setError(err.response?.data?.message || 'Failed to load issuance payment management requests');
        } finally {
            setLoading(false);
        }
    };

    const refreshCurrentPage = () => {
        fetchRequests({
            page: currentPage,
            limit: pageSize,
            status: statusFilter,
            search: debouncedSearch
        });
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const handleSearchInputChange = (value) => {
        setSearchQuery(value);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (value) => {
        setPageSize(value);
        setCurrentPage(1);
    };

    const goToPreviousPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        const maxPage = totalPages > 0 ? totalPages : 1;
        setCurrentPage(prev => Math.min(prev + 1, maxPage));
    };

    const openDetailsModal = (request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedRequest(null);
    };

    const handleFileSelection = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 10) {
            setModal({
                isOpen: true,
                type: 'error',
                title: 'Too Many Files',
                message: 'Maximum 10 files allowed',
                confirmText: 'OK',
                onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
            });
            return;
        }
        setSelectedFiles(files);
    };

    const handleUploadPaymentReference = async () => {
        if (!selectedRequest || selectedFiles.length === 0) {
            setModal({
                isOpen: true,
                type: 'error',
                title: 'No Files Selected',
                message: 'Please select files to upload',
                confirmText: 'OK',
                onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
            });
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            selectedFiles.forEach((file) => {
                formData.append('paymentReferenceFiles', file);
            });

            // Simulate progress for user feedback
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 200);

            await burnCashTokenRequestsAPI.uploadPaymentReference(selectedRequest._id, formData);

            clearInterval(progressInterval);
            setUploadProgress(100);

            setModal({
                isOpen: true,
                type: 'success',
                title: 'Success',
                message: 'Payment reference files uploaded successfully',
                confirmText: 'OK',
                onConfirm: () => {
                    setSuccessMessage('Payment reference files uploaded successfully');
                    setTimeout(() => setSuccessMessage(null), 5000);
                    closeUploadModal();
                    setUploadProgress(0);
                    refreshCurrentPage();
                    setModal(prev => ({ ...prev, isOpen: false }));
                }
            });
        } catch (err) {
            setModal({
                isOpen: true,
                type: 'error',
                title: 'Upload Failed',
                message: err.response?.data?.message || 'Failed to upload payment reference files',
                confirmText: 'OK',
                onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
            });
        } finally {
            setIsUploading(false);
        }
    };

    const openUploadModal = (request) => {
        setSelectedRequest(request);
        setSelectedFiles([]);
        setShowUploadModal(true);
    };

    const closeUploadModal = () => {
        setShowUploadModal(false);
        setSelectedFiles([]);
        setSelectedRequest(null);
    };

    const handleApproveL1 = (request) => {
        // Check if payment reference is uploaded
        if (!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) {
            setModal({
                isOpen: true,
                type: 'error',
                title: 'Payment Reference Required',
                message: 'Payment reference must be uploaded before L1 approval. Please upload the payment reference first.',
                confirmText: 'OK',
                onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
            });
            return;
        }

        setModal({
            isOpen: true,
            type: 'warning',
            title: 'Approve at L1',
            message: 'Are you sure you want to approve this issuance payment reference at L1? This will move the request to L2 approval.',
            confirmText: 'Approve L1',
            onConfirm: async () => {
                try {
                    await burnCashTokenRequestsAPI.approveL1(request._id);
                    setSuccessMessage('Request approved at L1 successfully');
                    setTimeout(() => setSuccessMessage(null), 5000);
                    refreshCurrentPage();
                    setModal(prev => ({ ...prev, isOpen: false }));
                } catch (err) {
                    setModal({
                        isOpen: true,
                        type: 'error',
                        title: 'Approval Failed',
                        message: err.response?.data?.message || 'Failed to approve request at L1',
                        confirmText: 'OK',
                        onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
                    });
                }
            }
        });
    };

    const handleApproveL2 = (request) => {
        // Check if payment reference is uploaded
        if (!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) {
            setModal({
                isOpen: true,
                type: 'error',
                title: 'Payment Reference Required',
                message: 'Payment reference must be uploaded before L2 approval. Please upload the payment reference first.',
                confirmText: 'OK',
                onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
            });
            return;
        }

        setModal({
            isOpen: true,
            type: 'warning',
            title: 'Approve at L2 - Burn Cash Tokens',
            message: `Are you sure you want to approve this issuance payment reference at L2? This will burn ${request.totalAmount} cash tokens from the issuer's wallet. This action cannot be undone.`,
            confirmText: 'Approve L2 & Burn',
            onConfirm: async () => {
                try {
                    setModal({
                        isOpen: true,
                        type: 'info',
                        title: 'Processing',
                        message: 'Burning cash tokens... Please wait.',
                        confirmText: null
                    });

                    await burnCashTokenRequestsAPI.approveL2(request._id);

                    setModal({
                        isOpen: true,
                        type: 'success',
                        title: 'Success',
                        message: 'Request approved at L2 and cash tokens burned successfully',
                        confirmText: 'OK',
                        onConfirm: () => {
                            setSuccessMessage('Cash tokens burned successfully');
                            setTimeout(() => setSuccessMessage(null), 5000);
                            refreshCurrentPage();
                            setModal(prev => ({ ...prev, isOpen: false }));
                        }
                    });
                } catch (err) {
                    setModal({
                        isOpen: true,
                        type: 'error',
                        title: 'Approval Failed',
                        message: err.response?.data?.message || 'Failed to approve request at L2',
                        confirmText: 'OK',
                        onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
                    });
                }
            }
        });
    };



    const getStatusBadge = (status) => {
        const badges = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending L1' },
            pending_l2: { color: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Pending L2' },
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Completed' },
            rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' }
        };

        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="w-4 h-4 mr-1" />
                {badge.text}
            </span>
        );
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        if (!amount) return '0';
        return parseFloat(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getUserName = (user) => {
        if (!user) return 'N/A';
        return user.corporateInfo?.companyName ||
            user.corporateInfo?.legalEntityName ||
            `${user.personalInfo?.firstName || ''} ${user.personalInfo?.lastName || ''}`.trim() ||
            user.email ||
            'Unknown';
    };

    const getCurrencyLabel = (request) => {
        if (!request) return '';
        const currency = request.bondSnapshot?.currency || request.bondId?.currency || request.currency;
        return currency ? currency.trim() : '';
    };

    const getFileUrl = (file) => {
        if (!file) return '';
        return file.path || file.url || file.downloadUrl || '';
    };

    const selectedRequestCurrency = selectedRequest ? getCurrencyLabel(selectedRequest) : '';
    const resolvedTotalPages = totalPages > 0 ? totalPages : 1;
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="animate-spin h-8 w-8 text-blue-600" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center mb-2">
                    <Flame className="h-8 w-8 text-orange-600 mr-3" />
                    <h1 className="text-3xl font-bold text-gray-900">Issuance Payment Management</h1>
                </div>
                <p className="text-gray-600">
                    Manage issuance payment for bonds.
                </p>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-green-800">{successMessage}</span>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                    <XCircle className="h-5 w-5 text-red-600 mr-3" />
                    <span className="text-red-800">{error}</span>
                </div>
            )}

            {/* Filters */}
            <div className="mb-6 bg-white rounded-lg shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status Filter
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending L1</option>
                            <option value="pending_l2">Pending L2</option>
                            <option value="completed">Completed</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search
                        </label>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                            placeholder="Search by bond name, number, stock code, or ISIN..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Requests Table */}
            {requests.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No issuance payment management requests found</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Bond Details
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        PS Bond Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {requests.map((request) => {
                                    const currencyLabel = getCurrencyLabel(request);
                                    return (
                                        <tr key={request._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {request.bondSnapshot?.bondName || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    {request.bondSnapshot?.stockCode || 'N/A'} | {request.bondSnapshot?.isin || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-bold text-gray-900">
                                                    {currencyLabel ? `${currencyLabel} ${formatAmount(request.totalAmount)}` : formatAmount(request.totalAmount)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(request.status)}
                                                {request.paymentReferenceFiles && request.paymentReferenceFiles.length > 0 && (
                                                    <div className="mt-1">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                            Payment Ref Uploaded
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {formatDate(request.createdAt)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {/* Upload Payment Reference - Always show for pending/pending_l2 if not uploaded */}
                                                    {(request.status === 'pending' || request.status === 'pending_l2') &&
                                                        (!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) && (
                                                            <button
                                                                onClick={() => openUploadModal(request)}
                                                                
                                                                className="inline-flex items-center justify-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                                                            >
                                                                <Upload className="h-3 w-3 mr-1" />
                                                                Upload
                                                            </button>
                                                        )}

                                                    {/* L1 Actions */}
                                                    {request.status === 'pending' && (
                                                        <button
                                                            onClick={() => handleApproveL1(request)}
                                                            
                                                            className={`px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 whitespace-nowrap ${(!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            title={(!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) ? 'Upload payment reference first' : ''}
                                                        >
                                                            Approve L1
                                                        </button>
                                                    )}

                                                    {/* L2 Actions */}
                                                    {request.status === 'pending_l2' && (
                                                        <button
                                                            onClick={() => handleApproveL2(request)}
                                                            
                                                            className={`inline-flex items-center px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 whitespace-nowrap ${(!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            title={(!request.paymentReferenceFiles || request.paymentReferenceFiles.length === 0) ? 'Upload payment reference first' : ''}
                                                        >
                                                            <Flame className="h-3 w-3 mr-1" />
                                                            L2 & Burn
                                                        </button>
                                                    )}

                                                    {/* View Details - Always show */}
                                                    <button
                                                        onClick={() => openDetailsModal(request)}
                                                        className="inline-flex items-center justify-center px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 whitespace-nowrap"
                                                    >
                                                        <FileText className="h-3 w-3 mr-1" />
                                                        Details
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {totalItems > 0 && (
                <div className="mt-4 bg-white rounded-lg shadow px-4 py-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-gray-600">
                        Showing {startItem}-{endItem} of {totalItems} results
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span>Rows per page:</span>
                            <div className="relative">
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="pr-8 pl-3 py-1 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    style={{
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        backgroundImage: 'none'
                                    }}
                                >
                                    {[10, 25, 50].map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 justify-between sm:justify-start">
                            <button
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-700">
                                Page {currentPage} of {resolvedTotalPages}
                            </span>
                            <button
                                onClick={goToNextPage}
                                disabled={currentPage >= resolvedTotalPages}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedRequest && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white my-10">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4 pb-4 px-5 border-b">
                                <div className="flex items-center">
                                    <FileText className="h-8 w-8 text-blue-600" />
                                    <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
                                        Issuance Payment Reference Details
                                    </h3>
                                </div>
                                <button
                                    onClick={closeDetailsModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="px-5 max-h-[70vh] overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Bond Information */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                                            <Flame className="h-4 w-4 mr-2 text-orange-600" />
                                            Bond Information
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div>
                                                <span className="text-gray-600 font-medium">Bond Name:</span>
                                                <div className="text-gray-900">{selectedRequest.bondSnapshot?.bondName || 'N/A'}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Stock Code:</span>
                                                <div className="text-gray-900">{selectedRequest.bondSnapshot?.stockCode || 'N/A'}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">ISIN:</span>
                                                <div className="text-gray-900">{selectedRequest.bondSnapshot?.isin || 'N/A'}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Issue Date:</span>
                                                <div className="text-gray-900">{formatDate(selectedRequest.bondSnapshot?.issueDate)}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Maturity Date:</span>
                                                <div className="text-gray-900">{formatDate(selectedRequest.bondSnapshot?.maturityDate)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Request Information */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-700 mb-3">Request Information</h4>
                                        <div className="space-y-2 text-sm">
                                            <div>
                                                <span className="text-gray-600 font-medium">Status:</span>
                                                <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">PS Bond Amount:</span>
                                                <div className="text-gray-900 font-bold text-lg">
                                                    {selectedRequestCurrency ? `${selectedRequestCurrency} ${formatAmount(selectedRequest.totalAmount)}` : formatAmount(selectedRequest.totalAmount)}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Issuer Wallet:</span>
                                                <div className="text-gray-900 font-mono text-xs break-all">
                                                    {selectedRequest.issuerWalletAddress}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Created At:</span>
                                                <div className="text-gray-900">{formatDate(selectedRequest.createdAt)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payment Reference Files */}
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                                            <Upload className="h-4 w-4 mr-2 text-blue-600" />
                                            Payment Reference Files
                                        </h4>
                                        {selectedRequest.paymentReferenceFiles && selectedRequest.paymentReferenceFiles.length > 0 ? (
                                            <div className="space-y-2">
                                                {selectedRequest.paymentReferenceFiles.map((file, index) => (
                                                    <div key={index} className="bg-white p-3 rounded border border-blue-200">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div>
                                                                <div className="text-sm font-medium text-blue-900">
                                                                    {index + 1}. {file.originalName || file.filename}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    Uploaded: {formatDate(file.uploadedAt)}
                                                                </div>
                                                            </div>
                                                            {getFileUrl(file) && (
                                                                <div className="flex flex-col gap-1 text-xs whitespace-nowrap">
                                                                    <a
                                                                        href={getFileUrl(file)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:text-blue-800"
                                                                    >
                                                                        Preview
                                                                    </a>
                                                                    <a
                                                                        href={getFileUrl(file)}
                                                                        download
                                                                        className="text-gray-600 hover:text-gray-800"
                                                                    >
                                                                        Download
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-600 flex items-center">
                                                <AlertTriangle className="h-4 w-4 mr-2 text-yellow-600" />
                                                No payment reference files uploaded
                                            </div>
                                        )}
                                    </div>

                                    {/* L1 Approval */}
                                    {selectedRequest.l1ApprovedBy && (
                                        <div className="bg-green-50 p-4 rounded-lg">
                                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                                                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                                L1 Approval
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600 font-medium">Approved By:</span>
                                                    <div className="text-gray-900">{getUserName(selectedRequest.l1ApprovedBy)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 font-medium">Approved At:</span>
                                                    <div className="text-gray-900">{formatDate(selectedRequest.l1ApprovedAt)}</div>
                                                </div>
                                                {selectedRequest.l1Notes && (
                                                    <div>
                                                        <span className="text-gray-600 font-medium">Notes:</span>
                                                        <div className="text-gray-900 bg-white p-2 rounded mt-1">{selectedRequest.l1Notes}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* L2 Approval */}
                                    {selectedRequest.l2ApprovedBy && (
                                        <div className="bg-green-50 p-4 rounded-lg">
                                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                                                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                                L2 Approval
                                            </h4>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600 font-medium">Approved By:</span>
                                                    <div className="text-gray-900">{getUserName(selectedRequest.l2ApprovedBy)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 font-medium">Approved At:</span>
                                                    <div className="text-gray-900">{formatDate(selectedRequest.l2ApprovedAt)}</div>
                                                </div>
                                                {selectedRequest.l2Notes && (
                                                    <div>
                                                        <span className="text-gray-600 font-medium">Notes:</span>
                                                        <div className="text-gray-900 bg-white p-2 rounded mt-1">{selectedRequest.l2Notes}</div>
                                                    </div>
                                                )}
                                                {selectedRequest.transactionHash && (
                                                    <div>
                                                        <span className="text-gray-600 font-medium">Transaction Hash:</span>
                                                        <div className="text-gray-900 font-mono text-xs break-all bg-white p-2 rounded mt-1">
                                                            {selectedRequest.transactionHash}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Rejection Details */}
                                    {selectedRequest.rejectedBy && (
                                        <div className="md:col-span-2 bg-red-50 p-4 rounded-lg">
                                            <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                                                <XCircle className="h-4 w-4 mr-2" />
                                                Rejection Details
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600 font-medium">Rejected By:</span>
                                                    <div className="text-gray-900">{getUserName(selectedRequest.rejectedBy)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 font-medium">Rejected At:</span>
                                                    <div className="text-gray-900">{formatDate(selectedRequest.rejectedAt)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600 font-medium">Rejection Level:</span>
                                                    <div className="text-gray-900">{selectedRequest.rejectionLevel}</div>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="text-gray-600 font-medium">Reason:</span>
                                                    <div className="text-gray-900 bg-white p-2 rounded mt-1">{selectedRequest.rejectionReason}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-gray-200 mt-6 pt-4 px-5">
                                <button
                                    onClick={closeDetailsModal}
                                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Payment Reference Modal */}
            {showUploadModal && selectedRequest && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4 pb-4 px-5">
                                <div className="flex items-center">
                                    <Upload className="h-8 w-8 text-blue-600" />
                                    <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
                                        Upload Payment Reference
                                    </h3>
                                </div>
                                <button
                                    onClick={closeUploadModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="border-b border-gray-200 mb-4"></div>

                            <div className="px-5">
                                <p className="text-sm text-gray-600 mb-4">
                                    Bond: <span className="font-medium text-gray-900">{selectedRequest.bondSnapshot?.bondName}</span>
                                </p>
                                <p className="text-sm text-gray-600 mb-4">
                                    PS Bond Amount: <span className="font-medium text-gray-900">{getCurrencyLabel(selectedRequest)} {formatAmount(selectedRequest.totalAmount)}</span>
                                </p>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Files (Max 10 files)
                                    </label>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileSelection}
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        className="block w-full text-sm text-gray-500
                                            file:mr-4 file:py-2 file:px-4
                                            file:rounded-md file:border-0
                                            file:text-sm file:font-semibold
                                            file:bg-blue-50 file:text-blue-700
                                            hover:file:bg-blue-100"
                                    />
                                    {selectedFiles.length > 0 && (
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-700">Selected files:</p>
                                            <ul className="mt-1 text-xs text-gray-600">
                                                {selectedFiles.map((file, index) => (
                                                    <li key={index} className="truncate">
                                                        • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {isUploading && (
                                    <div className="mt-4">
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1 text-center">{uploadProgress}% complete</p>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-gray-200 mt-6 pt-4 px-5">
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={closeUploadModal}
                                        disabled={isUploading}
                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUploadPaymentReference}
                                        disabled={selectedFiles.length === 0 || isUploading}
                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader className="animate-spin h-5 w-5 mr-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            'Upload Files'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            <Modal
                isOpen={modal.isOpen}
                onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onConfirm={modal.onConfirm}
                confirmText={modal.confirmText}
                cancelText={modal.cancelText}
                showInput={modal.showInput}
                inputPlaceholder={modal.inputPlaceholder}
            />
        </div>
    );
};

export default IssuancePaymentManagement;
